import React, { useState, useEffect, useCallback } from 'react'
import debounce from 'lodash/debounce'
import { Pagination } from '@mui/material'
import axios from 'axios'
import SectionTitle from '../../shared/sectionTitle'
import { LocalizationContextType } from '../../shared/contexts'
import { formatTenantDateTime } from '../../../utils/formatTenantDateTime'
import { TIME_SOURCE, timeSourceTitle } from '../../../utils/timeSource'
import CreateVoucherModal from './CreateVoucherModal'
import InvalidateVoucherModal from './InvalidateVoucherModal'
import VoucherOutcome from '../../shared/VoucherOutcome'
import { voucherReasonLabel, voucherReasonDetail } from '../../shared/voucherLabels'

interface Voucher {
  id: number
  token: string
  status: string
  reason: string
  initiator: string | null
  issued_by: string | null
  chain_depth: number
  note: string | null
  customer_phone: string | null
  source_order_id: number | null
  source_order_number: string | null
  source_order_external_id: string | null
  source_meal_sequence_number: number | null
  source_meal_disposal_reason: string | null
  redeemed_order_id: number | null
  redeemed_order_number: string | null
  expires_at: string | null
  redeemed_at: string | null
  invalidated_at: string | null
  invalidated_reason: string | null
  invalidated_by: string | null
  invalidation_note: string | null
  created_at: string | null
}

interface Props {
  localization_context?: LocalizationContextType
  can_create_voucher?: boolean
  can_invalidate_voucher?: boolean
}

// Desk staff read the source order id off the voucher and then want the order
// itself, so the number doubles as the link to its details page.
const sourceOrderLink = (voucher: Voucher) => {
  const label = voucher.source_order_external_id || voucher.source_order_number
  if (!label) return ''
  if (!voucher.source_order_id) return label

  return <a href={`/orders/${voucher.source_order_id}`}>{label}</a>
}

const VouchersList: React.FC<Props> = ({
  localization_context: localizationContext,
  can_create_voucher: canCreate,
  can_invalidate_voucher: canInvalidate,
}) => {
  const [vouchers, setVouchers] = useState<Voucher[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageCount, setPageCount] = useState(1)
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get('search') || '')
  const [error, setError] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [invalidating, setInvalidating] = useState<Voucher | null>(null)

  const handleSearch = useCallback(
    debounce((value: string) => {
      setSearch(value)
      setCurrentPage(1)
    }, 300),
    []
  )

  const fetchVouchers = () => {
    axios
      .get('/admin/vouchers', { params: { page: currentPage, search }, headers: { Accept: 'application/json' } })
      .then(({ data }) => {
        setVouchers(data.vouchers)
        setPageCount(data.pageCount || 1)
        setCurrentPage(data.page || 1)
      })
  }

  useEffect(() => {
    fetchVouchers()
  }, [currentPage, search])

  const resendSms = (voucher: Voucher) => {
    setError(null)
    axios
      .post(`/admin/vouchers/${voucher.id}/resend_sms`)
      .then(() => window.alert(`Voucher SMS re-sent to ${voucher.customer_phone || 'the customer'}.`))
      .catch((err) => setError(err.response?.data?.error || 'Could not resend the voucher SMS.'))
  }

  const applyInvalidation = (voucher: Voucher, updated: Record<string, unknown>) => {
    setVouchers((prev) => prev.map((v) => (v.id === voucher.id ? { ...v, ...updated } : v)))
    setInvalidating(null)
  }

  const formatDateTime = (iso: string | null) => formatTenantDateTime(iso, localizationContext)

  return (
    <>
      {invalidating && (
        <InvalidateVoucherModal
          voucher={invalidating}
          onClose={() => setInvalidating(null)}
          onInvalidated={(updated) => applyInvalidation(invalidating, updated)}
        />
      )}
      {showCreate && (
        <CreateVoucherModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false)
            fetchVouchers()
          }}
        />
      )}
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <SectionTitle rows={['All', 'Vouchers']} />
        <div className="d-flex align-items-center gap-2">
          {canCreate && (
            <button
              type="button"
              className="btn btn-primary"
              style={{ whiteSpace: 'nowrap' }}
              onClick={() => setShowCreate(true)}
            >
              Create voucher
            </button>
          )}
          <input
            style={{ width: '340px', flexShrink: 0 }}
            type="search"
            name="q"
            className="form-control"
            autoComplete="off"
            placeholder="Search by token, order id / number, or phone"
            defaultValue={search}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <table className="table table-robis table-order-queue">
        <thead className="thead-robis">
          <tr>
            <th>Token</th>
            <th>Status</th>
            <th>Reason</th>
            <th>Customer</th>
            <th>Source order</th>
            <th>Created at</th>
            <th>Expires</th>
            <th>Outcome</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {vouchers.map((voucher) => (
            <tr key={voucher.id}>
              <td>{voucher.token}</td>
              <td>{voucher.status}</td>
              <td>
                <div>{voucherReasonLabel(voucher)}</div>
                {voucherReasonDetail(voucher) && (
                  <div className="text-muted small">{voucherReasonDetail(voucher)}</div>
                )}
                {voucher.note && <div className="text-muted small">{voucher.note}</div>}
              </td>
              <td>{voucher.customer_phone}</td>
              <td>
                {sourceOrderLink(voucher)}
                {voucher.source_meal_sequence_number ? ` (meal #${voucher.source_meal_sequence_number})` : ''}
              </td>
              <td title={timeSourceTitle(voucher.created_at, TIME_SOURCE.app)}>
                {voucher.created_at ? formatTenantDateTime(voucher.created_at, localizationContext) : ''}
              </td>
              <td title={timeSourceTitle(voucher.expires_at, TIME_SOURCE.app)}>
                {voucher.expires_at ? formatTenantDateTime(voucher.expires_at, localizationContext) : ''}
              </td>
              <td><VoucherOutcome voucher={voucher} formatDateTime={formatDateTime} /></td>
              <td>
                <div className="d-flex flex-column gap-2">
                  {voucher.status === 'active' && voucher.customer_phone && canCreate && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      style={{ whiteSpace: 'nowrap' }}
                      onClick={() => resendSms(voucher)}
                    >
                      Resend SMS
                    </button>
                  )}
                  {voucher.status === 'active' && canInvalidate && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      style={{ whiteSpace: 'nowrap' }}
                      onClick={() => setInvalidating(voucher)}
                    >
                      Invalidate
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Pagination
        count={pageCount}
        size="large"
        page={currentPage}
        variant="outlined"
        shape="rounded"
        onChange={(_e, page) => setCurrentPage(page)}
      />
    </>
  )
}

export default VouchersList
